import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client.js';

/**
 * Topbar search — same shape as CliLancers' ⌘K input.
 *
 * Click the input OR press Cmd/Ctrl-K from anywhere → modal opens with
 * live results from /api/search. Arrow keys navigate, Enter to follow,
 * Esc to close.
 *
 * Results are grouped: posts first (most common search target), then
 * projects. Each row is a real Link target — Enter triggers navigation
 * by simulating a click.
 *
 * Open state is owned by AppShell rather than held here, because the mobile
 * nav drawer needs to open this modal too (the trigger button is hidden
 * below 768px, and ⌘K is not reachable on a phone keyboard).
 */
export default function SearchBar({ open, onOpenChange }) {
  const setOpen = onOpenChange;
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global ⌘K / Ctrl+K shortcut
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // Focus the input when the modal opens
  useEffect(() => {
    if (open) {
      // Defer focus to next tick — input mounts inside the modal
      setTimeout(() => inputRef.current?.focus(), 0);
      setCursor(0);
    } else {
      setQ('');
    }
  }, [open]);

  const searchQ = useQuery({
    queryKey: ['search', q],
    queryFn: ({ signal }) => apiGet(`/search?q=${encodeURIComponent(q)}`, { signal }),
    enabled: q.trim().length >= 2,
    staleTime: 10_000,
  });

  // Flatten results into a single ordered array so arrow-key navigation
  // works across groups without needing per-group cursor state.
  const flat = useMemo(() => {
    const data = searchQ.data || { posts: [], projects: [] };
    return [
      ...data.posts.map((p) => ({ kind: 'post', id: p.id, label: p.title || '(no title)', sub: `${p.project_name} · ${p.pillar_key.replace(/_/g, ' ')}`, href: `/posts/${p.id}` })),
      ...data.projects.map((pr) => ({ kind: 'project', id: pr.id, label: pr.display_name, sub: pr.ig_handle || pr.positioning?.slice(0, 60), href: `/projects/${pr.key}` })),
    ];
  }, [searchQ.data]);

  function follow(item) {
    setOpen(false);
    navigate(item.href);
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && flat[cursor]) { e.preventDefault(); follow(flat[cursor]); }
  }

  return (
    <>
      <button
        type="button"
        className="search-trigger"
        onClick={() => setOpen(true)}
        aria-label="Search"
      >
        <span className="search-trigger-icon" aria-hidden="true">🔍</span>
        <span className="search-trigger-label">Search…</span>
        <span className="search-trigger-kbd"><kbd>⌘</kbd><kbd>K</kbd></span>
      </button>

      {open && (
        <div className="search-modal-scrim" onClick={() => setOpen(false)}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-input-row">
              <span className="search-input-icon">🔍</span>
              <input
                ref={inputRef}
                className="search-input"
                placeholder="Search posts, projects…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setCursor(0); }}
                onKeyDown={onKeyDown}
              />
              <kbd className="search-input-esc">esc</kbd>
            </div>

            <div className="search-results">
              {q.trim().length < 2 && (
                <div className="search-hint">Type at least 2 characters to search posts and projects.</div>
              )}

              {searchQ.isLoading && q.trim().length >= 2 && (
                <div className="search-hint">Searching…</div>
              )}

              {searchQ.data && q.trim().length >= 2 && flat.length === 0 && !searchQ.isLoading && (
                <div className="search-hint">No matches for “{q}”.</div>
              )}

              {flat.length > 0 && (
                <ul className="search-result-list">
                  {(searchQ.data?.posts?.length || 0) > 0 && (
                    <li className="search-group-head">Posts</li>
                  )}
                  {searchQ.data?.posts?.map((p, i) => {
                    const idx = i;
                    return (
                      <li
                        key={p.id}
                        className={`search-result ${cursor === idx ? 'active' : ''}`}
                        onMouseEnter={() => setCursor(idx)}
                        onClick={() => follow({ href: `/posts/${p.id}` })}
                      >
                        <div className="search-result-main">{p.title || <em>(no title)</em>}</div>
                        <div className="search-result-sub">{p.project_name} · {p.pillar_key.replace(/_/g, ' ')} · {p.state}</div>
                      </li>
                    );
                  })}
                  {(searchQ.data?.projects?.length || 0) > 0 && (
                    <li className="search-group-head">Projects</li>
                  )}
                  {searchQ.data?.projects?.map((pr, i) => {
                    const idx = (searchQ.data?.posts?.length || 0) + i;
                    return (
                      <li
                        key={pr.id}
                        className={`search-result ${cursor === idx ? 'active' : ''}`}
                        onMouseEnter={() => setCursor(idx)}
                        onClick={() => follow({ href: `/projects/${pr.key}` })}
                      >
                        <div className="search-result-main">{pr.display_name}</div>
                        <div className="search-result-sub">{pr.ig_handle || pr.positioning?.slice(0, 80)}</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="search-footer">
              <span className="muted small">
                <kbd>↑</kbd><kbd>↓</kbd> navigate · <kbd>↵</kbd> open · <kbd>esc</kbd> close
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
